import { useLocale } from '@solinkcorp/locale';
import {
  Alert,
  AlertDescription,
  Input,
  SelectItem,
  SimpleFormField,
  SimpleSelect,
  cn,
} from '@solinkcorp/solink-ui';
import React from 'react';
import { type UseFormReturn } from 'react-hook-form';
import { type WidgetFormData } from './schema';

interface CountWidgetSettingsProps {
  form: UseFormReturn<WidgetFormData>;
  /** Whether the widget has at least one day-part-specific threshold override set. */
  hasDayPartThresholds: boolean;
  /** Whether the customer has at least one day part defined. */
  hasAnyDayParts: boolean;
}

/**
 * Shows settings specific to count widgets.
 *
 * This is intended to be displayed as a child of `WidgetDialog`.
 */
export const CountWidgetSettings: React.FC<CountWidgetSettingsProps> = ({
  form,
  hasDayPartThresholds,
  hasAnyDayParts,
}) => {
  const locale = useLocale();
  const isDriveOff = form.watch('type') === 'DRIVE_OFF';
  const horizon = form.watch('horizon');
  const showDayPartFallbackWarning =
    horizon === 'currentDayPart' && hasAnyDayParts && !hasDayPartThresholds;
  return (
    <>
      <SimpleFormField
        className={`${form.watch('type') === 'DRIVE_OFF' ? 'hidden' : ''}`}
        control={form.control}
        name="countThreshold"
        label={locale.serviceDisplayMonitor.countThreshold}
        render={({ field }) => (
          <Input
            type="number"
            data-testid="sdm-widget-count-threshold-input"
            className={cn(
              'focus-visible:border-teal-500 focus-visible:ring-[3px] focus-visible:ring-teal-500/50',
            )}
            {...field}
            onChange={(e) => field.onChange(+e.currentTarget.value)}
          />
        )}
      />

      <SimpleFormField
        control={form.control}
        name="horizon"
        label={locale.serviceDisplayMonitor.horizon}
        description={locale.serviceDisplayMonitor.chooseTimeHorizon}
        render={({ field }) => (
          <SimpleSelect className="w-[100%]" data-testid="sdm-widget-horizon-select" {...field}>
            {!isDriveOff && (
              <SelectItem value="realtime">{locale.serviceDisplayMonitor.realTime}</SelectItem>
            )}
            <SelectItem value="15m">{locale.serviceDisplayMonitor.sumOfLast15Minutes}</SelectItem>
            <SelectItem value="1h">{locale.serviceDisplayMonitor.sumOfLastHour}</SelectItem>
            <SelectItem value="currentHour">
              {locale.serviceDisplayMonitor.sumOfCurrentHour}
            </SelectItem>
            <SelectItem value="currentDay">
              {locale.serviceDisplayMonitor.sumOfCurrentDay}
            </SelectItem>
            {hasAnyDayParts && (
              <SelectItem value="currentDayPart">
                {locale.serviceDisplayMonitor.sumOfCurrentDayPart}
              </SelectItem>
            )}
          </SimpleSelect>
        )}
      />
      {showDayPartFallbackWarning && (
        <Alert variant="destructive" className="mt-2 border-border">
          <AlertDescription
            className="text-text-secondary"
            data-testid="day-part-no-association-warning"
          >
            {locale.serviceDisplayMonitor.currentDayPartNoAssociationWarning}
          </AlertDescription>
        </Alert>
      )}
    </>
  );
};
